/* eslint-disable no-unused-vars */
/* React */
import React, { useContext, useEffect, useMemo, useState } from 'react';

/* Router */
import { useRouter } from 'routes/index';

/* Translation */
import { useTranslation } from 'react-i18next';

/* Components */
import { Checkbox, Modal } from 'lego-dist';
import AddTeacherView from 'views/NewAddTeacherView';
import AddTeacherSuccess from 'views/NewAddTeacherView/SuccessView';
import TeacherList from './TeacherList';

/* Styled Components */
import { CenterTab } from './styles/CenterGeneral';
import {
  TeacherHeader,
  TeacherCount,
  TeacherActions,
  SearchBar,
  SearchIcon,
  AddTeacherBtnMobile,
  AddTeacherBtn,
  StylesImageModalResultsMovedTeacher,
} from './styles/TeacherTab';
import SelectAllCheckbox from '../sharedComponents/styles/SelectAllCheckbox';

/* Services, Hooks, Constants */
import { debounce } from 'lib/utils';
import { APP_SECTIONS_PATHS } from 'constants/routes';
import { AppContext } from 'App';
import useTeacher from 'hooks/useTeacher';
import useCenters from 'hooks/centers/UseCenters';
import RendererIf from 'components/RendererIf';
import themes from 'themes';
import MoveTeacher from './MoveTeacher/MoveTeacher';
import teacherMovedErrorImage from 'images/error.svg';
import teacherAddErrorImage from 'images/error.svg';
import teacherMovedSuccessImage from 'images/checked.svg';
import teacherAddSuccessImage from 'images/checked.svg';
import ResultActionModal from 'components/shared/ResultActionModal/ResultActionModal';
import { ROLES_OBJECT } from 'constants/onBoarding';
import { ActionPermission } from 'lib/specialPermissions';
import useCurrentUser from 'hooks/useCurrentUser';

interface TeacherTabProps {
  teachers?: any;
  handleOnFinishAddTeacherFlow?: any;
  center?: any;
  refreshData?(): void;
}


const TeacherTab: React.FC<TeacherTabProps> = ({ teachers, handleOnFinishAddTeacherFlow, center: currentCenter, refreshData }) => {
  const { t, i18n } = useTranslation();
  const { history, currentParamsRef } = useRouter();
  const { fetchCenterById } = useCenters();
  const { addTeacher, moveTeachers } = useTeacher();
  const { isFreemiumUser, isDemoUser } = useContext(AppContext);
  const [filteredTeachers, setFilteredTeachers] = useState<any[]>([]);
  const { can } = useCurrentUser();

  useEffect(() => setFilteredTeachers(teachers), [teachers]);

  const {
    current: { centerId },
  } = currentParamsRef;

  /* select teachers logic */
  const [selectedTeachers, setSelectedTeachers] = useState<any[]>([]);

  /* toggle add or move teacher */
  const [moveTeacher] = useState(true);
  const [openMoveTeachersModal, setOpenMoveTeachersModal] = useState(false);

  /* open modals */
  const [openAddTeacherModal, setOpenAddTeacherModal] = useState(false);
  const [openAddTeacherSuccess, setOpenAddTeacherSuccess] = useState(false);
  const [showResultActionMoveTeacherModal, setShowResultActionMoveTeacherModal] = useState<any>(
    false,
  );
  const [showResultActionAddTeacherModal, setShowResultActionAddTeacherModal] = useState<any>(
    false,
  );

  const ConfigResultActionMoveTeacherModal = {
    success: {
      title: t(
        'settings:CENTER_SECTION.MOVE_TEACHER.MODAL_RESULT.SUCCESS_TITLE',
      ),
      image: teacherMovedSuccessImage,
      configPopsModal: {
        showCloseButton: false,
        onClosedModal: () => {
          if (setShowResultActionMoveTeacherModal) setShowResultActionMoveTeacherModal(false);
          if(refreshData) refreshData();
        },
      },
    },
    error: {
      title: t('settings:ERROR'),
      image: teacherMovedErrorImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          if (setShowResultActionMoveTeacherModal) setShowResultActionMoveTeacherModal(false);
        },
      },
    },
  };

  const ConfigResultActionAddTeacherModal = {
    success: {
      title: t(
        'settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.MODAL_RESULT.TEACHER',
      ),
      image: teacherAddSuccessImage,
      configPopsModal: {
        showCloseButton: false,
        onClosedModal: () => {
          if (setShowResultActionAddTeacherModal) setShowResultActionAddTeacherModal(false);
          if (handleOnFinishAddTeacherFlow) handleOnFinishAddTeacherFlow();
        },
      },
    },
    error: {
      title: t('settings:ERROR'),
      image: teacherAddErrorImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          if (setShowResultActionAddTeacherModal) setShowResultActionAddTeacherModal(false);
        },
      },
    },
  };

  const [statusResultActionMoveTeacherModal, setStatusResultActionMoveTeacherModal] = useState<any>(
    ConfigResultActionMoveTeacherModal.success,
  );

  const [statusResultActionAddTeacherModal, setStatusResultActionAddTeacherModal] = useState<any>(
    ConfigResultActionAddTeacherModal.error,
  );

  /* ADD TEACHER, MOVE TEACHER functions */
  const handleAddTeacherFlow = () => {
    if (isFreemiumUser || isDemoUser) {
      history.push(APP_SECTIONS_PATHS.PREMIUM_PROCESS);
    } else {
      setOpenAddTeacherModal(true);
    }
  };

  const handleOnFinishFlow = (values: any) => {
    const {
      general_info: generalInfo,
      permissions,
      centersWithClassrooms
    } = values;

    const form = {
      user: {
        permission_ids: permissions.permissions,
        role_alias: permissions.role_alias,
        center_ids: centersWithClassrooms.centers.map((center: any) => typeof(center) === "number" ? center : center.id).filter((center: any) => !!center),
        group_ids: centersWithClassrooms?.classrooms?.map((classroom: any) => typeof(classroom) === "number" ? classroom : classroom.id),
        ...generalInfo,
        type: ROLES_OBJECT.TEACHER.value,
        title: ROLES_OBJECT.TEACHER.value,
        locale: i18n.language,
      },
    };

    addTeacher(form, centerId)
      .then((response) => {
        if (response instanceof Error) throw response;
        fetchCenterById(centerId);
        setStatusResultActionAddTeacherModal(ConfigResultActionAddTeacherModal.success);
      })
      .catch((err) => {
        let text = undefined;
        if (err.response?.status === 422 && err.response?.data?.errors?.email) text = t('EMAIL_ALREADY_TAKEN');
        setStatusResultActionAddTeacherModal({ text, ...ConfigResultActionAddTeacherModal.error});
      })
      .finally(() => {
        setOpenAddTeacherModal(false);
        setShowResultActionAddTeacherModal(true);
      });
  };

  /* SEARCH functions */
  const handleSearch = debounce((value: any) => {
    const pattern = new RegExp(`${value}`, 'i');
    const filterResult = teachers.filter(
      (teacher: any) => teacher.name && pattern.test(teacher.name),
    );
    setFilteredTeachers(filterResult);
  }, 300);


  const handleSelectAllTeachers = (checked: boolean) => {
    if(checked) {
      setSelectedTeachers(filteredTeachers);
    } else {
      setSelectedTeachers([]);
    }
  };

  const toggleSelectTeacher = (teacher: any) => {
    let currentSelected = [...selectedTeachers];
    if (currentSelected.find((item: any) => item.id === teacher.id)) {
      currentSelected = currentSelected.filter((item: any) => item.id !== teacher.id);
    } else {
      currentSelected.push(teacher);
    }
    setSelectedTeachers(currentSelected);
  };

  const handleOnClickMoveTeacher = (centers: any, groups: any) => {
    setOpenMoveTeachersModal(false);
    moveTeachers({
      teachers: selectedTeachers.map((t: any) => t.id),
      sendToCenter: centers.map((c: any) => ({
        id: c?.id,
        group_ids: groups.filter((g: any) => g?.parent?.id === c?.id)?.map((g: any) => g?.id)
      })),
      centerId: currentCenter?.id,
    }).then((response: any) => {
      setStatusResultActionMoveTeacherModal(ConfigResultActionMoveTeacherModal[response?.isAxiosError ? 'error' : 'success']);
    }).catch(() => {
      setStatusResultActionMoveTeacherModal(ConfigResultActionMoveTeacherModal.error);
    }).finally(() => setShowResultActionMoveTeacherModal(true));
  };

  const hasAllChecked = useMemo(() => {
    return selectedTeachers.length === filteredTeachers.length
  }, [selectedTeachers, filteredTeachers]);

  const hasSomeItemsChecked = useMemo(() => {
    const hasCheckedAtLeastAnItem = selectedTeachers.length > 0;

    return hasCheckedAtLeastAnItem && !hasAllChecked;
  }, [selectedTeachers, hasAllChecked]);

  return (
    <CenterTab>
      <RendererIf condition={showResultActionMoveTeacherModal}>
        <ResultActionModal
          styles={{ Image: StylesImageModalResultsMovedTeacher }}
          {...statusResultActionMoveTeacherModal}
        />
      </RendererIf>
      <RendererIf condition={showResultActionAddTeacherModal}>
        <ResultActionModal
          styles={{ Image: StylesImageModalResultsMovedTeacher }}
          {...statusResultActionAddTeacherModal}
        />
      </RendererIf>
      <TeacherHeader>
        <TeacherCount>
          <RendererIf condition={can(ActionPermission.MOVE_TEACHERS_FROM_CENTER)}>
            <SelectAllCheckbox someChecked={hasSomeItemsChecked}>
              <Checkbox
                label=" "
                name="permissions"
                checked={hasAllChecked}
                onChange={(checked: boolean) => handleSelectAllTeachers(checked)}
              />
            </SelectAllCheckbox>
          </RendererIf>
          {`${t('settings:TEACHERS')} (${teachers.length})`}
        </TeacherCount>
        <TeacherActions>
          <RendererIf
            condition={!selectedTeachers?.length}
            alternative={(
              <RendererIf condition={can(ActionPermission.MOVE_TEACHERS_FROM_CENTER)}>
                <AddTeacherBtn
                  rounded
                  flat
                  fill
                  color={themes.colors.newKinedu}
                  onClick={() => setOpenMoveTeachersModal(true)}
                >
                  {t('MOVE_TO')}
                </AddTeacherBtn>
              </RendererIf>
            )}
          >
            <>
              <SearchBar
                theme="Rounded"
                placeholder={t('settings:STAFF_SECTION.MANAGE.SEARCH')}
                onChange={handleSearch}
                rightSlot={() => <SearchIcon />}
              />
              <RendererIf condition={can(ActionPermission.ADD_TEACHERS)}>
                <>
                  <AddTeacherBtnMobile
                    rounded
                    flat
                    fill
                    color="#75B753"
                    onClick={() => handleAddTeacherFlow()}
                  >
                    +
                  </AddTeacherBtnMobile>
                  <AddTeacherBtn
                    rounded
                    flat
                    fill
                    color="#75B753"
                    onClick={() => handleAddTeacherFlow()}
                  >
                    {t('settings:ADD_TEACHER')}
                  </AddTeacherBtn>
                </>
              </RendererIf>
            </>
          </RendererIf>
        </TeacherActions>
      </TeacherHeader>
      <TeacherList
        toggleSelectTeacher={toggleSelectTeacher}
        selectedTeachers={selectedTeachers}
        teachers={filteredTeachers.length > 0 ? filteredTeachers : teachers}
        handleOnFinishDeleteTeacherFlow={handleOnFinishAddTeacherFlow}
      />
      <Modal
        openModal={openAddTeacherModal}
        title={t('settings:ADD_TEACHER')}
        headerColor={themes.colors.newKinedu}
        closeButtonColor="#ffffff"
        confirmCloseModal
        confirmCloseModalTexts={{
          title: t('activities:IF_YOU_LEAVE.TITLE'),
          button_leave: t('activities:IF_YOU_LEAVE.BUTTON_LEAVE'),
          button_stay: t('activities:IF_YOU_LEAVE.BUTTON_STAY'),
          description: t('activities:IF_YOU_LEAVE.DESCRIPTION'),
          warning: t('activities:IF_YOU_LEAVE.WARNING')
        }}
        persistent
        onClosedModal={() => {
          setOpenAddTeacherModal(false);
        }}
      >
        {() => <AddTeacherView onFinishFlow={handleOnFinishFlow} />}
      </Modal>
      <Modal
        openModal={openAddTeacherSuccess}
        onClosedModal={() => {
          setOpenAddTeacherSuccess(false);
        }}
      >
        {() => (
          <AddTeacherSuccess
            caption={moveTeacher ? 'Moved Teacher' : t('settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.MODAL_RESULT.TEACHER')}
          />
        )}
      </Modal>
      <Modal
        openModal={openMoveTeachersModal}
        headerColor={themes.colors.newKinedu}
        title={t('settings:CENTER_SECTION.MOVE_TEACHER.TITLE')}
        onClosedModal={() => {
          setOpenMoveTeachersModal(false);
        }}
      >
        {() => <MoveTeacher handleOnClickMoveTeacher={handleOnClickMoveTeacher} />}
      </Modal>
    </CenterTab>
  );
};

export default TeacherTab;
